import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Send, Loader2, RotateCw } from "lucide-react";
import { RevenueChart } from "./RevenueChart";
import { TopClients } from "./TopClients";
import { Input } from "@/components/ui/input";
import { adminApi, invoicePdfUrl } from "@/lib/api";
import { fmtDate, money, selectCls } from "./shared";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const ResendButton = ({ l, onSent }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!window.confirm(`Reinviare il riepilogo di ${MONTHS[l.month - 1]} ${l.year} al commercialista con copia all'admin?`)) return;
    setBusy(true);
    try { const r = await adminApi.sendMonthlyInvoices(l.year, l.month); toast.success(`Riepilogo reinviato (${r.count} fatture)`); onSent(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore reinvio"); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid={`inv-log-resend-${l.sent_at}`} onClick={go} disabled={busy}
      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase border border-[#d5dbde] hover:border-[#E0262C] hover:text-[#E0262C] px-2 py-1 disabled:opacity-40">
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />} Reinvia
    </button>
  );
};

const ReportLog = ({ logs, onSent }) => (
  <div data-testid="inv-report-log" className="border border-[#d5dbde]">
    <div className="px-4 py-3 bg-[#f4f6f7] text-[10px] uppercase tracking-widest font-mono text-[#6b7280]">Storico invii al commercialista</div>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono border-t border-[#d5dbde]">
          {["Mese", "Inviato il", "Fatture", "Tipo", "Destinatari", "Excel", "Azioni"].map((h) => <th key={h} className="px-4 py-2">{h}</th>)}
        </tr>
      </thead>
      <tbody>
        {logs.map((l) => (
          <tr key={l.sent_at} data-testid={`inv-log-${l.period}`} className="border-t border-[#d5dbde]">
            <td className="px-4 py-2 font-medium">{MONTHS[l.month - 1]} {l.year}</td>
            <td className="px-4 py-2 font-mono text-xs text-[#5b6168]">{fmtDate(l.sent_at, true)}</td>
            <td className="px-4 py-2 font-display">{l.count}</td>
            <td className="px-4 py-2"><span className={`text-[10px] font-mono uppercase px-2 py-0.5 ${l.mode === "auto" ? "bg-[#3b82f6] text-white" : "bg-[#e4e7e9] text-[#5b6168]"}`}>{l.mode === "auto" ? "Automatico" : "Manuale"}</span></td>
            <td className="px-4 py-2 text-xs font-mono space-y-0.5">
              {l.recipients.map((r) => <div key={r.email} className={r.ok ? "text-[#5b6168]" : "text-[#ef4444]"}>{r.ok ? "✓" : "✗"} {r.email}{r.copy ? " (copia)" : ""}</div>)}
            </td>
            <td className="px-4 py-2">{l.xlsx_url && <a href={l.xlsx_url} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase border border-[#d5dbde] hover:border-[#E0262C] px-2 py-1"><Download className="w-3 h-3" /> xlsx</a>}</td>
            <td className="px-4 py-2"><ResendButton l={l} onSent={onSent} /></td>
          </tr>
        ))}
        {!logs.length && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6b7280] font-mono text-xs">Nessun riepilogo inviato</td></tr>}
      </tbody>
    </table>
  </div>
);

const Filters = ({ f, setF, years, onSent }) => (
  <div className="flex flex-wrap gap-3 items-center">
    <Input data-testid="inv-search" placeholder="Cerca numero, cliente, ragione sociale..." value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })}
      className="h-10 w-80 bg-[#f4f6f7] border-[#d5dbde] text-[#2B3036] rounded-sm text-sm" />
    <select data-testid="inv-year" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value, month: e.target.value ? f.month : "" })} className={selectCls}>
      <option value="">Tutti gli anni</option>
      {years.map((y) => <option key={y} value={y}>{y}</option>)}
    </select>
    <select data-testid="inv-month" value={f.month} disabled={!f.year} onChange={(e) => setF({ ...f, month: e.target.value })} className={`${selectCls} disabled:opacity-40`}>
      <option value="">Tutti i mesi</option>
      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
    </select>
    <a data-testid="inv-export-csv" href={adminApi.invoicesCsvUrl(f)}
      className="ml-auto h-10 inline-flex items-center gap-2 border border-[#d5dbde] hover:border-white text-[#2B3036] px-4 text-sm font-medium">
      <Download className="w-4 h-4" /> CSV
    </a>
    <a data-testid="inv-export-xlsx" href={adminApi.invoicesXlsxUrl(f)}
      className="h-10 inline-flex items-center gap-2 border border-[#d5dbde] hover:border-white text-[#2B3036] px-4 text-sm font-medium">
      <Download className="w-4 h-4" /> Excel
    </a>
    <SendMonthlyButton year={f.year} month={f.month} onSent={onSent} />
  </div>
);

const SendMonthlyButton = ({ year, month, onSent }) => {
  const [busy, setBusy] = useState(false);
  const ready = year && month;
  const send = async () => {
    if (!window.confirm(`Inviare le fatture di ${MONTHS[month - 1]} ${year} al commercialista (gm@pixawebch.com) con copia a info@graficawebdesign.com?`)) return;
    setBusy(true);
    try { const r = await adminApi.sendMonthlyInvoices(year, month); toast.success(`Inviate ${r.count} fatture a ${r.to} (copia a ${r.cc})`); onSent(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore invio"); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid="inv-send-accountant" onClick={send} disabled={!ready || busy} title={ready ? "" : "Scegli anno e mese"}
      className="h-10 inline-flex items-center gap-2 aurora-gradient text-white px-4 text-sm font-medium hover:opacity-90 disabled:opacity-40">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Invia al commercialista
    </button>
  );
};

export const InvoicesPanel = () => {
  const [f, setF] = useState({ q: "", year: "", month: "" });
  const [data, setData] = useState({ invoices: [], totals: {}, years: [], count: 0 });
  useEffect(() => {
    const t = setTimeout(() => adminApi.invoices(Object.fromEntries(Object.entries(f).filter(([, v]) => v)))
      .then(setData).catch(() => toast.error("Errore caricamento fatture")), 250);
    return () => clearTimeout(t);
  }, [f]);
  const [logs, setLogs] = useState([]);
  const loadLogs = () => adminApi.reportLog().then((d) => setLogs(d.logs)).catch(() => {});
  useEffect(() => { loadLogs(); }, []);
  const years = data.years.length ? data.years : [new Date().getFullYear()];

  return (
    <div data-testid="admin-invoices" className="space-y-4">
      <div className="grid grid-cols-3 border border-[#d5dbde] divide-x divide-[#d5dbde]">
        <div className="p-5"><div className="text-[10px] uppercase font-mono text-[#6b7280]">Fatture nel periodo</div><div data-testid="inv-count" className="font-display text-3xl mt-2">{data.count}</div></div>
        {["eur", "chf"].map((c) => (
          <div key={c} className="p-5"><div className="text-[10px] uppercase font-mono text-[#6b7280]">Totale {c.toUpperCase()}</div>
            <div data-testid={`inv-total-${c}`} className="font-display text-3xl mt-2 aurora-text">{money(data.totals[c] || 0, c)}</div></div>
        ))}
      </div>
      <Filters f={f} setF={setF} years={years} onSent={loadLogs} />
      <RevenueChart year={f.year} />
      <TopClients filters={f} />
      <div className="border border-[#d5dbde] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono bg-[#f4f6f7]">
              {["Numero", "Data", "Cliente", "Descrizione", "Sconto", "Totale", "PDF"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.invoices.map((i) => (
              <tr key={i.number} data-testid={`inv-row-${i.number}`} className="border-t border-[#d5dbde] hover:bg-[#f4f6f7]">
                <td className="px-4 py-3 font-mono">{i.number}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#5b6168]">{fmtDate(i.issued_at)}</td>
                <td className="px-4 py-3"><div>{i.billing?.company || i.customer_name}</div><div className="font-mono text-xs text-[#6b7280]">{i.customer_email}{i.billing?.vat_id ? ` · ${i.billing.vat_id}` : ""}</div></td>
                <td className="px-4 py-3 text-xs text-[#5b6168] max-w-[260px] truncate">{i.description}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#34d399]">{i.discount_code ? `${i.discount_code} · -${Number(i.discount_amount).toFixed(2)}` : "—"}</td>
                <td className="px-4 py-3 font-display aurora-text">{money(i.total, i.currency)}</td>
                <td className="px-4 py-3"><a data-testid={`inv-pdf-${i.number}`} href={invoicePdfUrl(i.campaign_id)} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase border border-[#d5dbde] hover:border-[#E0262C] px-2 py-1"><FileText className="w-3 h-3" /> PDF</a></td>
              </tr>
            ))}
            {!data.invoices.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6b7280] font-mono text-xs">Nessuna fattura nel periodo</td></tr>}
          </tbody>
        </table>
      </div>
      <ReportLog logs={logs} onSent={loadLogs} />
    </div>
  );
};
